import type {
  ProductDisplayCatalog,
  ProductDisplayDefinition,
  ProductSurfacePath,
} from './deskUiSkeletons/editor/ProductDisplayCatalogContext'
import { productCreationContractForProduct } from '@/contracts/productCreation'
import type {
  RowVariantMetadata,
  RowVariantMetadataRegistry,
} from './deskUiSkeletons/rowTemplates/RowTemplateTypes'
import { sharedChatAnswerVariantMetadata } from './deskUiSkeletons/rowTemplates/RowVariantRegistry'

function choice(
  value: RowVariantMetadata['value'],
  viewType: RowVariantMetadata['viewType'],
  label: string,
  variant: RowVariantMetadata['variant'],
): RowVariantMetadata {
  return { value, viewType, label, variant }
}

function rows(
  localVariants: readonly RowVariantMetadata[],
  answersFallback: RowVariantMetadataRegistry['answers']['fallback'],
  submsgesFallback = answersFallback,
): RowVariantMetadataRegistry {
  return {
    answers: {
      variants: [...localVariants, sharedChatAnswerVariantMetadata],
      fallback: answersFallback,
    },
    submsges: {
      variants: localVariants,
      fallback: submsgesFallback,
    },
  }
}

function commentSlideRows(label: string, slideVariant: RowVariantMetadata['variant']) {
  return rows([
    choice(2, 'comments', `${label} Comment`, 'comments'),
    choice(3, 'gallery', `${label} Slide`, slideVariant),
  ], 'comments')
}

const stdRows = rows([
  choice(1, 'compact', 'Legacy Compact', 'compact'),
  choice(2, 'comments', 'Legacy Comments', 'comments'),
  choice(3, 'gallery', 'Legacy Card', 'card'),
  choice(4, 'gallery_uno', 'Legacy Std', 'legacystd'),
  choice(6, 'gallery_width', 'Legacy Email', 'email'),
  choice(7, 'gallery_height', 'Cards Slide', 'cards'),
], 'comments', 'compact')

const musicboxRows: RowVariantMetadataRegistry = {
  answers: { variants: [choice(2, 'comments', 'Music Box contents and comments', 'comments'), sharedChatAnswerVariantMetadata], fallback: 'comments' },
  submsges: { variants: [choice(3, 'gallery', 'Nested Music Box', 'card')], fallback: 'card' },
}

const definitions: ProductDisplayCatalog = [
  { path: 'std', comboValue: 0, dbMarker: 'STD', runtimeMsgType: 'std', label: 'Standard', createContract: 'ordinary-v1', settingsSelectable: false, rowVariants: stdRows },
  { path: 'mailbox', comboValue: 1, dbMarker: 'MAIL_BOX', runtimeMsgType: 'mail_box', label: 'Mailbox', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: rows([choice(1, 'compact', 'Mailbox Hotmail', 'hotmail')], 'hotmail') },
  { path: 'forum', comboValue: 2, dbMarker: 'FORUM', runtimeMsgType: 'forum', label: 'Forum', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: rows([choice(2, 'comments', 'Forum PHPBB', 'phpbb')], 'phpbb') },
  { path: 'gallery', comboValue: 4, dbMarker: 'GALLERY', runtimeMsgType: 'gallery', label: 'Gallery', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Gallery', 'gallery') },
  { path: 'site', comboValue: 8, dbMarker: 'SITE', runtimeMsgType: 'site', label: 'Website', createContract: 'ordinary-v1', settingsSelectable: true },
  { path: 'contacts', comboValue: 10, dbMarker: 'CONTACTS', runtimeMsgType: 'contacts', label: 'Contacts', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Contacts', 'contacts') },
  { path: 'notes', comboValue: 11, dbMarker: 'NOTE', runtimeMsgType: 'note', label: 'Notes', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Notes', 'notes') },
  { path: 'poo', comboValue: 18, dbMarker: 'POO', runtimeMsgType: 'poo', label: 'Opening Proof Protocol', createContract: 'poo-v1', settingsSelectable: true, rowVariants: rows([choice(2, 'comments', 'OPP Comments', 'comments')], 'comments') },
  { path: 'gallery2', comboValue: 19, dbMarker: 'GALLERY2', runtimeMsgType: 'gallery2', label: 'Gallery 2', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Gallery 2', 'gallery') },
  { path: 'flickr', comboValue: 20, dbMarker: 'FLICKR', runtimeMsgType: 'flickr', label: 'Flickr', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Flickr', 'flickr') },
  { path: 'instagram', comboValue: 21, dbMarker: 'INSTAGRAM', runtimeMsgType: 'instagram', label: 'Instagram', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Instagram', 'instagram') },
  { path: 'gmail', comboValue: 22, dbMarker: 'GMAIL', runtimeMsgType: 'gmail', label: 'Gmail', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Gmail', 'gmail') },
  { path: 'twitter', comboValue: 23, dbMarker: 'TWITTER', runtimeMsgType: 'twitter', label: 'Twitter', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Twitter', 'twitter') },
  { path: 'facebook', comboValue: 24, dbMarker: 'FACEBOOK', runtimeMsgType: 'facebook', label: 'Facebook', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Facebook', 'facebook') },
  { path: 'hackernews', comboValue: 25, dbMarker: 'HACKERNEWS', runtimeMsgType: 'hackernews', label: 'Hacker News', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Hacker News', 'hackernews') },
  { path: 'fullscreen', comboValue: 26, dbMarker: 'FULLSCREEN', runtimeMsgType: 'fullscreen', label: 'Fullscreen', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Fullscreen', 'fullscreen') },
  { path: 'cards', comboValue: 27, dbMarker: 'CARDS', runtimeMsgType: 'cards', label: 'Cards', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Cards', 'cards') },
  { path: 'presentation', comboValue: 28, dbMarker: 'PRESENTATION', runtimeMsgType: 'presentation', label: 'Presentation', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Presentation', 'presentation') },
  { path: 'tiles', comboValue: 29, dbMarker: 'TILES', runtimeMsgType: 'tiles', label: 'Tiles', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: commentSlideRows('Tiles', 'tiles') },
  { path: 'wiki', comboValue: 30, dbMarker: 'WIKI', runtimeMsgType: 'wiki', label: 'Wiki', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: rows([choice(2, 'comments', 'Wiki Article', 'wiki')], 'wiki') },
  { path: 'email', comboValue: 31, dbMarker: 'EMAIL', runtimeMsgType: 'email', label: 'Email', createContract: 'ordinary-v1', settingsSelectable: false, internalOnly: true },
  { path: 'musicbox', comboValue: 32, dbMarker: 'MUSIC_BOX', runtimeMsgType: 'music_box', label: 'Music Box', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: musicboxRows },
  { path: 'musictrack', comboValue: 33, dbMarker: 'MUSIC_TRACK', runtimeMsgType: 'music_track', label: 'Music Track', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: rows([choice(2, 'comments', 'Music Track contents and comments', 'comments')], 'comments') },
  { path: 'portal', comboValue: 34, dbMarker: 'PORTAL', runtimeMsgType: 'portal', label: 'Portal', createContract: 'ordinary-v1', settingsSelectable: true, rowVariants: rows([choice(1, 'compact', 'Portal Result', 'dense')], 'dense') },
]

const definitionByPath = new Map(definitions.map(definition => [definition.path, definition]))

for (const definition of definitions) {
  const duplicates = definitions.filter(candidate => candidate.comboValue === definition.comboValue)
  if (duplicates.length !== 1) throw new Error(`Duplicate product Display value: ${definition.comboValue}`)
  const expectedCreateContract = productCreationContractForProduct(definition.path).id
  if (definition.createContract !== expectedCreateContract) {
    throw new Error(`Product creation contract mismatch: ${definition.path}:${definition.createContract}`)
  }
  const createContract = productCreationContractForProduct(definition.path)
  if (definition.settingsSelectable && !createContract.supportsConfigureCreate) {
    throw new Error(`Product creation contract is not Configure-selectable: ${definition.path}:${createContract.id}`)
  }
}

export const productDisplayCatalog = definitions

export function productDisplayDefinitionForPath(path: ProductSurfacePath): ProductDisplayDefinition {
  const definition = definitionByPath.get(path)
  if (!definition) throw new Error(`Product Display definition is missing: ${path}`)
  return definition
}

export function productDisplayComboValue(path: ProductSurfacePath): number {
  return productDisplayDefinitionForPath(path).comboValue
}

export function productRowVariantsForPath(path: ProductSurfacePath): RowVariantMetadataRegistry {
  const variants = productDisplayDefinitionForPath(path).rowVariants
  if (!variants) throw new Error(`Product surface has no row variants: ${path}`)
  return variants
}

export function productRowVariantForPath(
  path: ProductSurfacePath,
  value: RowVariantMetadata['value'],
  section: keyof RowVariantMetadataRegistry = 'answers',
): RowVariantMetadata {
  const variant = productRowVariantsForPath(path)[section].variants.find(candidate => candidate.value === value)
  if (!variant) throw new Error(`Product row variant is missing: ${path}:${section}:${value}`)
  return variant
}
